import type { SharedProps } from '@/frontend/types/inertia'
import { router, usePage } from '@inertiajs/react'
import { Check, ChevronsUpDown, Pencil, Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import ProjectMark from '@/frontend/features/Fde/ProjectMark'
import type { FdeCopy, Project } from '@/frontend/features/Fde/types'
import { fdeProjectPath } from '@/frontend/features/Fde/routes'

interface Props {
  path: string
  project: Project | null
  projects: Array<Pick<Project, 'id' | 'name' | 'description' | 'objective' | 'icon' | 'logo_url'> & { status: string; updated_at: string }>
  copy: FdeCopy
  canManage: boolean
  onEdit?: () => void
  onNew: () => void
}

export default function ProjectControls({ path, project, projects, copy, canManage, onEdit, onNew }: Props) {
  const { props } = usePage<SharedProps>()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR')
    return normalized ? projects.filter((item) => `${item.name} ${item.description || ''} ${item.objective || ''}`.toLocaleLowerCase('pt-BR').includes(normalized)) : projects
  }, [projects, query])

  if (props.featureFlags?.worker_first_nav_enabled && props.permissions?.can_access_forge) return null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="h-9 max-w-56 gap-1.5 px-2 text-foreground" aria-label={copy.workspace.switch_project}>
          {project && <ProjectMark project={project} className="size-7 rounded-md" />}
          <span className="truncate text-sm font-medium">{project?.name || copy.workspace.projects}</span>
          <ChevronsUpDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-border p-3">
          <p className="text-xs font-semibold text-foreground">{copy.workspace.projects}</p>
          <div className="relative mt-2"><Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.workspace.search_projects} className="h-9 pl-8" /></div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1.5">
          {filtered.map((item) => <button key={item.id} type="button" onClick={() => { setOpen(false); router.visit(fdeProjectPath(path, item.id)) }} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-muted">
            <ProjectMark project={item} />
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-foreground">{item.name}</span><span className="block truncate text-xs text-muted-foreground">{item.description || item.objective || copy.workspace.no_project_description}</span></span>
            {item.id === project?.id && <Check className="size-4 shrink-0 text-primary" />}
          </button>)}
          {filtered.length === 0 && <p className="px-3 py-6 text-center text-xs text-muted-foreground">{copy.workspace.no_project_match}</p>}
        </div>
        {canManage && <div className="flex items-center gap-2 border-t border-border p-2">
          {project && onEdit && <Button size="sm" variant="ghost" className="flex-1 justify-start" onClick={() => { setOpen(false); onEdit() }}><Pencil />{copy.workspace.edit_project}</Button>}
          <Button size="sm" variant="ghost" className="flex-1 justify-start" onClick={() => { setOpen(false); onNew() }}><Plus />{copy.workspace.new_project}</Button>
        </div>}
      </PopoverContent>
    </Popover>
  )
}
